import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { catchError, tap, map } from 'rxjs/operators';
import { ReplaySubject, Observable, EMPTY, BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Component as IComponent, ComponentDetail, ITimeAggregation } from 'app/shared/interfaces/component';
import { GetComponentByIdInterface, GetComponentsListInterface, GetComponentsViewInterface, PropertiesInterface, TagDetail, AlarmsConfigsInterface, TagInterface, ICompTimeAggregations, ITagsPropsName, getPropertiesByEntityId } from 'app/services/graphqlApi.service';
import { RestAPIService } from 'app/services/rest-api.service';
import { DataService } from 'app/shared/data/data.service';
import { CommonService } from 'app/shared/services/common.service';
import { getComponentById, getComponents, getComponentsView, getComponentTypesNVendors, getTagsNames, getTimeAggrRanges, getTimeAggrsByCompId, getTimeAggrsByModelId } from 'app/shared/constants/gql';
import { IAssetPropOverride } from 'app/assets/interfaces/interfaces';
import { Tag } from 'app/shared/tags/interfaces/tag';
import { EEntityTypes } from 'app/shared/enums';
import { getAlarm } from 'app/utils/alarms';
import { RestRequestType } from 'app/shared/enums/rest-request-type.enum';
import { IAddRefTypes, IDropdown } from 'app/shared/interfaces/model';

import { Apollo } from 'apollo-angular';
import { ApolloQueryResult } from 'apollo-client';

@Injectable({ providedIn: 'root' })

export class ComponentsService {
  constructor(
    private readonly apiService: RestAPIService,
    private readonly toastr: ToastrService,
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    readonly commonService: CommonService,
    private readonly dataService: DataService) { }

  private _componentsSource = new ReplaySubject<any[]>();
  readonly components$ = this._componentsSource.asObservable();

  private readonly _componentsViewSource = new BehaviorSubject<any[]>([]);
  readonly componentsView$ = this._componentsViewSource.asObservable();

  componentDetails = new BehaviorSubject<ComponentDetail>({} as ComponentDetail);
  readonly componentDetails$ = this.componentDetails.asObservable();

  private _componentsImageSource = new ReplaySubject<any[]>();
  readonly componentsWithImages$ = this._componentsImageSource.asObservable();

  // aggregation time ranges
  readonly _aggrTimeRanges = new BehaviorSubject<IDropdown[]>([]);
  readonly aggrTimeRanges$ = this._aggrTimeRanges.asObservable();

  // aggregation functions
  readonly _aggrFunctions = new BehaviorSubject<IDropdown[]>([]);
  readonly aggrFunctions$ = this._aggrFunctions.asObservable();

  // aggregations
  readonly _aggregations = new BehaviorSubject<ITimeAggregation[]>([]);
  readonly aggregations$ = this._aggregations.asObservable();

  CompState$ = new BehaviorSubject<any>(null);
  /**
   * Creates a new component
   *
   * @param name Name of component to create
   * @optional @param tags Optional array of tags that are created with the  component
   * @optional @param label Optional component label
   */
  createComponent(name: string, tags: Tag[] = [], labels: string[] = [], image: any = {}, componentTypeId: number, vendorId?: number, notes?: string, hasSystemTags?: boolean): Observable<{ componentID: string }> {
    return this.apiService.post<{ id: string }>(EEntityTypes.COMPONENTS, RestRequestType.CREATE_COMPONENT, { name, labels, tags, image, componentTypeId, vendorId, notes, hasSystemTags })
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Component created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => ({ componentID: res.data.id })));
  }

  /**
   * update component
   * @param component component data
   */
  updateComponent(component: IComponent) {
    return this.apiService.put<{ id: string }>(`components/${component.id}`, RestRequestType.UPDATE_COMPONENT, component)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success(this.translate.instant('Component.updateComponent'));
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => ({ componentID: res.data.id })));
  }

  /**
   * delete component
   * @param id component id
   */
  deleteComponent(id: string) {
    return this.apiService.delete(`components/${id}`)
      .toPromise().then(res => {
        this.toastr.success('Component deleted successfully!');
        this.getAllComponents();
        const updatedComponents: any[] = this._componentsViewSource.getValue().filter(component => component.id !== id);
        this._componentsViewSource.next([...updatedComponents]);
        return Promise.resolve();
      }, err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return Promise.reject(err.error);
      });
  }

  /**
   * delete component image
   * @param id component id
   */
  deleteComponentImage(id: string) {
    return this.apiService.delete(`components/${id}/image`)
      .toPromise().then(res => {
        this.toastr.success('Component image deleted successfully!');
        return Promise.resolve();
      }, err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return Promise.reject(err.error);
      });
  }

  /**
   * get component by id
   * @param id id
   */
  getComponentById(id: string) {
    return this.apiService.getGQLData({ query: getComponentById.query, params: { id: id } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetComponentByIdInterface[] = response?.data?.intellija_entities;
      const componentData = queryResponse.map(record => {
        return {
          id: record.id,
          name: record.name,
          type: record.components[0]?.ref_component_types_id,
          vendor: record.components[0]?.vendor_id,
          notes: record.components[0]?.notes,
          hasSystemTags: record.components[0]?.has_system_tags,
          createdBy: record.created_by,
          createdOn: record.created_date,
          lastEditedBy: record.updated_by,
          lastEditedOn: record.updated_date,
          labels: this.getLabels(record.labels || []),
          tags: this.getTags(record.tags || []),
          image: record.image,
          tagsCount: record.tags_aggregate?.aggregate?.count || 0,
          modelCount: record.entities_components?.length || 0,
          models: record.entities_components?.map(c => c.entityByEntityId) || [],
          properties: this.mapProperties(record.properties, record.id, record.name),
          alarmsCount: record.alarmsConfigsByComponentId?.length || 0,
          alarms: this.getAlarms(record.alarmsConfigsByComponentId),
        };
      });

      return componentData || [];
    }), tap(components => {
      this.dataService.tags.next(components[0]?.tags || []);
      // this.dataService._properties.next(components[0]?.properties || []); // will be open when props will be back
      this.dataService._properties.next(components[0]?.properties.length ? components[0]?.properties: this.dataService._properties.getValue().length ? this.dataService._properties.getValue() : []); // for temporary as backend not sending Properties
      this.dataService.alarms.next(components[0]?.alarms || []);
    }));
  }

  /**
   * get alarms
   * @param configs configs
   */
  getAlarms(configs: AlarmsConfigsInterface[]) {
    return configs.map(config => getAlarm(config));
  }

  /**
   * get tags
   * @param tags tags info
   */
  getTags(tags: TagInterface[]): TagDetail[] {
    // console.log('tags:>> ', tags);
    return tags.map(tag => {
      const states = (tag.state_tags || []).map(s =>
      ({
        color: s.color,
        value: s.value,
        isBlink: s.is_blink,
        id: s.id,
        state: s.state
      }));

      return {
        id: tag.id,
        allowWrite: tag.allow_write || false,
        entity_id: tag.entity_id,
        calc_tag_config: tag.calc_tag_config || null,
        name: tag.name,
        tagTypeId: tag.tag_type_id,
        tagType: tag.ref_tag_type?.name,
        displayName: tag.display_name,
        description: tag.description,
        alarms: null,
        action: null,
        historizedId: tag.historized_id,
        displayEuId: tag.display_eu_id,
        displayEuName: tag.ref_uom?.name ?? '',
        displayEuSymbol: tag.ref_uom?.symbol ?? '',
        factor: tag.factor,
        offset: tag.offset,
        precision: tag.precision,
        rejectMax: tag.reject_max,
        rejectMin: tag.reject_min,
        histPercentChange: tag.hist_percent_change,
        isThousandSeparator: tag.is_thousand_separator || false,
        measurementCategory: tag.measurement_category,
        states,
        alarmsCount: tag.alarms_configs?.length || 0,
        createdOn: tag.created_date,
        updatedOn: tag.updated_date,
      }
    });
  }

  /**
   * set component detail
   * @param id component id
   */
  setCompnentDetail(id: string) {
    this.getComponentById(id).subscribe(compList => {
      if (compList.length) {
        this.componentDetails.next(compList[0]);
        console.log('compList[0]?.tags :>> ', compList[0]?.tags);
        this.dataService.tags.next(compList[0]?.tags);
        this.dataService._properties.next(compList[0]?.properties);
      }
    })
  }

  /**
   * get all components
   */
  getAllComponents() {
    return this.apiService.getGQLData({ query: getComponents.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetComponentsListInterface[] = response?.data?.intellija_entities;
      const assetsData = queryResponse.map(record => {
        return {
          id: record.id,
          name: record.name,
          createdBy: record.created_by,
          createdOn: record.created_date,
          lastEditedBy: record.updated_by,
          lastEditedOn: record.updated_date,
          labels: this.getLabels(record.labels || []),
          tagsCount: record.tags_aggregate?.aggregate?.count || 0,
          modelCount: record.entities_components?.length || 0,
          componentTypeId: record.components[0]?.ref_component_type?.id,
          componentTypeName: record.components[0]?.ref_component_type?.name,
          vendorId: record.components[0]?.vendor?.id,
          vendorName: record.components[0]?.vendor?.name,
          models: record.entities_components?.map(c => c.entity) || [],
        };
      });

      console.log('assetsData: ', assetsData);
      this._componentsSource.next(assetsData);
    }));
  }

  /**
   * get components view data
   */
  getComponentsView() {
    return this.apiService.getGQLData({ query: getComponentsView.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const queryResponse: GetComponentsViewInterface[] = response?.data?.intellija_components_view;
      const componentsViewData = queryResponse.map(record => {
        return {
          id: record.comp_id,
          name: record.comp_name,
          alarmsCount: record.num_alarms,
          assetsCount: record.num_assets,
          modelsCount: record.num_models,
          tagsCount: record.num_tags,
          createdBy: record.created_by,
          createdOn: record.created_date,
          updatedBy: record.updated_by,
          updatedOn: record.updated_date,
          vendor: record.vendor,
          type: record.component_type,
          labels: record.labels?.map((labelNames) => labelNames.name)
        };
      });

      this._componentsViewSource.next(componentsViewData);
    }));
  }

  /**
   * get components with images
   */
  getAllComponentsWithImages() {
    return this.apiService.getGQLData({ query: getComponents.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response: any) => {
      const queryResponse: GetComponentsListInterface[] = response?.data?.intellija_entities;
      const assetsData = queryResponse.map(record => {
        return {
          id: record.id,
          name: record.name,
          createdBy: record.created_by,
          createdOn: record.created_date,
          lastEditedBy: record.updated_by,
          lastEditedOn: record.updated_date,
          labels: this.getLabels(record.labels || []),
          tagsCount: record.tags_aggregate.aggregate.count,
          modelCount: record.modelCount.aggregate.count,
          image: record.image != null ? record.image.image : 'showDefaultPreview'
        };
      });

      this._componentsImageSource.next(assetsData);
      return assetsData;
    }));
  }

  getPropertiesByEntityId(entityId: string) {
    return this.apollo.watchQuery({ query: getPropertiesByEntityId, variables: {entityId} })
      .valueChanges.pipe(map((response: ApolloQueryResult<any>) => (response.data?.intellija_properties || []).map(p => ({
        id: p.id,
        name: p.name,
        value: p.value,
        dataTypeId: p.data_type,
        propertyType: p.ref_property_type?.name,
        action: null,
        createdBy: p.created_by,
        createdOn: p.created_date,
        lastEditedBy: p.updated_by,
        lastEditedOn: p.updated_date,
        entity_id: p.entity_id,
        entityId,
        entityType: EEntityTypes.COMPONENTS,
        disableDeleteAction: false
      }))));
    }

  /**
   * map properties
   * @param properties proeprties
   * @param entityId entity id
   * @param entityName entity name
   */
  mapProperties(properties: PropertiesInterface[], entityId: string, entityName: string) {
    return (properties || []).map(p => ({
      id: p.id,
      name: p.name,
      value: p.value,
      dataTypeId: p.data_type,
      propertyType: p.ref_property_type?.name,
      action: null,
      createdBy: p.created_by,
      createdOn: p.created_date,
      lastEditedBy: p.updated_by,
      lastEditedOn: p.updated_date,
      entity_id: p.entity_id,
      entityName,
      entityId,
      entityType: EEntityTypes.COMPONENTS,
      disableDeleteAction: false
    }));
  }

  /**
   * get labels
   * @param labels labels
   */
  private getLabels(labels): string[] {
    return labels.reduce((result, l) => {
      if (l.label?.name) {
        result.push(l.label.name);
      }

      return result;
    }, []);
  }

  /**
   * get component types and vendors
   */
  getComponentTypesNVendors() {
    return this.apiService.getGQLData({ query: getComponentTypesNVendors.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) =>
      (response.data || [])
    ));
  }

  /**
   * get time aggr ranges to populate dropdown
   */
  getTimeAggrRanges() {
    return this.apiService.getGQLData({ query: getTimeAggrRanges.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) =>
      (response.data?.intellija_ref_tag_time_ranges || []).map(range => {
        return { name: range.display_name, value: range.id } as IDropdown
      })));
  }

  /**
   * get time aggrs to populate component-> aggr grid
   */
  getTimeAggrsByCompId(componentId: string) {
    return this.apiService.getGQLData({ query: getTimeAggrsByCompId.query, params: { componentId: componentId } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) =>
      (response.data?.intellija_aggr_configs || []).map((aggr: ICompTimeAggregations) => this.setTimeAggregations(aggr, '', '', EEntityTypes.COMPONENTS))));
  }

  /**
 * get time aggrs to populate model-> aggr grid
 */
  getTimeAggrsByModelId(modelId: string) {
    return this.apiService.getGQLData({ query: getTimeAggrsByModelId.query, params: { modelId: modelId } }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      const modelId: string = response.data?.intellija_entities[0]?.id;
      const modelsAggragtion: ITimeAggregation[] = (response.data?.intellija_entities[0].aggr_configs || []).map((mAggr: ICompTimeAggregations) => this.setTimeAggregations(mAggr, '', '', EEntityTypes.MODELS, modelId))
      const componentAggragation: ITimeAggregation[] = [];
      response.data?.intellija_entities[0].entitiesComponentsByEntityId[0].model_component_instances.map((compInstance) => {
        (compInstance.component_instance.entity.aggr_configs || []).map((aggr: ICompTimeAggregations) => {
          const componentId: string = response.data?.intellija_entities[0].entitiesComponentsByEntityId[0].component_id;
          componentAggragation.push(this.setTimeAggregations(aggr, compInstance.component_instance_id, compInstance.name, EEntityTypes.COMPONENTS, componentId));
        });
      });

      return [...componentAggragation, ...modelsAggragtion];
    }));
  }

  /**
   * set time aggregations view data
   * @param aggr aggregation
   * @param ciId CI id
   * @param ciName CI name
   * @param entityType entity type (models or components)
   */
  setTimeAggregations(aggr: ICompTimeAggregations, ciId: string, ciName: string, entityType: string, entityId?: string): ITimeAggregation {
    return {
      id: aggr.id,
      name: aggr.name,
      tags: aggr.tags,
      tagName: '',
      aggrConfigs: aggr.aggr_configs,
      timeRangeId: aggr.ref_tag_time_range.id,
      timeRangeName: aggr.ref_tag_time_range.display_name,
      aggrType: aggr.ref_agg_type.display_name,
      funcConfig: aggr.func_config,
      aggrFuncId: aggr.ref_agg_function.id,
      aggrFuncName: aggr.ref_agg_function.name,
      createdBy: aggr.created_by,
      createdDate: aggr.created_date,
      updatedBy: aggr.updated_by,
      updatedDate: aggr.updated_date,
      compInstanceId: ciId,
      compInstanceName: ciName,
      entityType: entityType,
      entityId: entityId ?? '',
      disableDeleteAction: (entityType === EEntityTypes.COMPONENTS && ciId) ? true : false
    }
  }

  /**
   * get tags names
   */
  getTagsNames() {
    return this.apiService.getGQLData({ query: getTagsNames.query }).pipe(catchError(err => {
      const message = err.error.error?.message || 'An error has occured.';
      this.toastr.error(message);
      return EMPTY;
    }), map((response) => {
      return (response.data?.intellija_tags || []) as ITagsPropsName[]
    }));
  }

  /**
   * add component type
   * @param type type's name
   */
  addComponentType(type: IAddRefTypes) {
    return this.apiService.post(`component-types`, RestRequestType.CREATE_COMPONENT_TYPE, type)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }),
        tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Component-Type created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }),
        map(res => res.data));
  }

  /**
   * add vendor type
   * @param type type's name
   */
  addVendorType(type: IAddRefTypes) {
    return this.apiService.post(`vendors`, RestRequestType.CREATE_COMPONENT_TYPE, type)
      .pipe(catchError(err => {
        const message = err.error.error?.message || 'An error has occured.';
        this.toastr.error(message);
        return EMPTY;
      }), tap(res => {
          if (res.result.status === 'ok') {
            this.toastr.success('Vendor created successfully!');
          } else {
            this.toastr.error(res.error.message);
          }
        }), map(res => res.data));
  }

}
